import { setUser } from '@sentry/react';
import { setUserId, setGroup, identify as amplitudeIdentify, Identify } from '@amplitude/analytics-browser';
import { Role } from 'API';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import LoadingOverlay from 'app/shared-components/util/LoadingOverlay';
import { DEFAULT_LANDING_PAGE, identifyMobileAppUser } from 'app/store/authSlice';
import { useFeatureFlags } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { sha256 } from 'util/hash';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useLogger } from 'util/logging';
import { useAuth, useUserCompanies } from './AuthProvider';

const ROLE_MAP: Record<string, string> = {
  [Role.ADMINISTRATOR]: 'admin',
  // SEE-946: analytics labels for the new roles.
  [Role.SECONDARY_ADMINISTRATOR]: 'secondary_admin',
  [Role.APPROVAL_ADMINISTRATOR]: 'approval_admin',
  [Role.GROUP_ADMINISTRATOR]: 'groupadmin',
  [Role.CARDHOLDER]: 'cardholder',
};

export interface RequireAuthProps {
  children: React.ReactNode;
  requireUser?: boolean;
  requireOnboarded?: boolean;
  isMobile?: boolean;
  /**
   * Hold the spinner until the paidol's feature flags have loaded. Defaults to
   * true. Set false for full-screen post-login pages that render on an
   * authenticated user alone and read no flags (SEE-213 passkey prompt) — the
   * flags gate is otherwise NOT tied to requireOnboarded, so it would briefly
   * flash a spinner over such a page the moment the paidol id resolves.
   */
  requireFeatureFlags?: boolean;
}

function RequireAuth({
  children,
  requireUser = true,
  requireOnboarded = true,
  isMobile = false,
  requireFeatureFlags = true,
}: RequireAuthProps): JSX.Element {
  const doesRequireOnboarded = requireUser && requireOnboarded;
  const { user, didSessionExpire, isAuthStateUnknown, didUserSignOut } = useAuth();
  const { isUserCompaniesStateUnknown, isUserOnboarded, selectedPaidolId, selectedPaidol, userRole } =
    useUserCompanies();
  const { areFeatureFlagsLoaded } = useFeatureFlags();

  // Hold the spinner until the full auth+data sequence is complete:
  //   1. user exists (getCurrentUser resolved)
  //   2. companies loaded AND a paidol is selected (getUserCompanies + setSelectedPaidolId resolved)
  //   3. feature flags loaded for that paidol (getRemoteFlags resolved)
  // Step 2 requires both — companies loading without a selected paidol is an intermediate state
  // that causes a visible flash if we release the spinner too early.
  const isPaidolLoading =
    doesRequireOnboarded && !!user?.sub && (!selectedPaidolId || isUserCompaniesStateUnknown);
  const isFlagsLoading = requireFeatureFlags && !!user?.sub && !!selectedPaidolId && !areFeatureFlagsLoaded;

  const location = useLocation();
  const locationState = location.state as { from?: Location };
  const rawFrom = locationState?.from?.pathname;
  const rawSearch = locationState?.from?.search ?? '';
  // Never redirect back to logout or login — use landing page instead.
  // Keep the query string so a bookmarked deep link (e.g. advanced-filter
  // URLs) survives the login redirect. SEE-1198.
  const from =
    rawFrom && !rawFrom.startsWith('/logout') && !rawFrom.startsWith('/login')
      ? `${rawFrom}${rawSearch}`
      : DEFAULT_LANDING_PAGE;
  const { enqueueSnackbar } = useSnackbar();
  const logger = useLogger();

  useEffect(() => {
    if (user) {
      const identity = {
        email: user?.email,
        id: user?.sub,
        username: user.preferred_username,
      };
      setUser(identity);
      // Relay the same identity to the native app so its device-rich Sentry
      // events become attributable to this customer. No-op outside the app.
      identifyMobileAppUser(identity);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.sub) return;

    (async () => {
      try {
        const hashedUserId = await sha256(user.sub);
        setUserId(hashedUserId);

        if (selectedPaidolId) {
          const hashedCustomerId = await sha256(selectedPaidolId);
          setGroup('customer', hashedCustomerId);

          const erpValue = selectedPaidol?.isAgaveClient
            ? selectedPaidol?.agaveSourceSystemSlug
            : selectedPaidol?.erpSpeedchain && selectedPaidol?.erpConnectionId
            ? 'erpConnection'
            : selectedPaidol?.isProcoreClient
            ? 'procore'
            : selectedPaidol?.flexCodingSettings?.isEnabled
            ? 'flex'
            : undefined;
          if (erpValue) {
            setGroup('erp', erpValue);
          }
        }

        if (userRole) {
          const mappedRole = ROLE_MAP[userRole];
          if (mappedRole) {
            amplitudeIdentify(new Identify().set('role', mappedRole));
          }
        }
      } catch (error) {
        logger.warn('Failed to set Amplitude identity:', error);
      }
    })();
  }, [
    user?.sub,
    selectedPaidolId,
    selectedPaidol?.isAgaveClient,
    selectedPaidol?.agaveSourceSystemSlug,
    selectedPaidol?.erpSpeedchain,
    selectedPaidol?.erpConnectionId,
    selectedPaidol?.isProcoreClient,
    selectedPaidol?.flexCodingSettings?.isEnabled,
    userRole,
    logger,
  ]);

  useEffect(() => {
    if (requireUser && didSessionExpire) {
      enqueueSnackbar('You have been logged out. Please log in again.');
    }
  }, [didSessionExpire, enqueueSnackbar, requireUser]);

  if (
    isFlagsLoading ||
    isPaidolLoading ||
    (!user?.sub && (isAuthStateUnknown || (doesRequireOnboarded && isUserCompaniesStateUnknown)))
  ) {
    return (
      <>
        {isMobile ? (
          <MobileLoadingOverlay isLoading={true} />
        ) : (
          <LoadingOverlay isLoading={true} blockInteraction={true} />
        )}
      </>
    );
  }

  if (requireUser && !user) {
    const redirectPath = '/login';
    return <Navigate to={redirectPath} state={{ from: didUserSignOut ? undefined : location }} replace />;
  } else if (!requireUser && user) {
    return <Navigate to={from} replace />;
  }

  if (doesRequireOnboarded && !isUserOnboarded && !isUserCompaniesStateUnknown) {
    return <Navigate to="/review/onboard" replace />;
  }

  return <>{children}</>;
}

export default RequireAuth;
